import RemoveFacilityTemplateOverride from '@Application/Documents/TemplateAssignments/Commands/RemoveFacilityTemplateOverride';
import removeFacilityTemplateOverride from '@Application/Documents/TemplateAssignments/removeFacilityTemplateOverride';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';
import { X } from 'lucide-react';
import * as React from 'react';

type Props = {
	readonly overrideId: number;
	readonly organizationId: number;
	readonly facilityName: string;
};

/**
 * Leaf action, dispatches itself. Distinct from `TemplateAssignmentCardActions`' own "Retirer la
 * surcharge": that one clears the facility's assignment from the facility screen itself
 * (`TEMPLATE_ASSIGNMENT_CLEARED`), this one removes the same override from the organization screen's
 * own view of it (`FACILITY_TEMPLATE_OVERRIDE_REMOVED`) — two different routes, two different actions,
 * over the same underlying row.
 */
const RemoveFacilityOverrideConfirmationDialog: React.FC<Props> = ({
	overrideId,
	organizationId,
	facilityName,
}) => {
	const dispatch = useDispatch();
	const [isVisible, setVisible] = React.useState<boolean>(false);

	const handleOpen = React.useCallback(() => {
		setVisible(true);
	}, []);

	const handleHide = React.useCallback(() => {
		setVisible(false);
	}, []);

	const handleAccept = React.useCallback(() => {
		dispatch(
			removeFacilityTemplateOverride(new RemoveFacilityTemplateOverride(organizationId, overrideId)),
		).catch(ignoreHandledError);
		setVisible(false);
	}, [dispatch, organizationId, overrideId]);

	return (
		<React.Fragment>
			<Button
				type="button"
				severity="danger"
				text
				tooltip="Retirer la surcharge"
				tooltipOptions={{ position: 'top' }}
				icon={<X size={14} />}
				onClick={handleOpen}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isVisible}
				onHide={handleHide}
				header="Retirer la surcharge"
				message={
					<p>
						Retirer la surcharge de l’agence {facilityName} ? Elle utilisera le gabarit de la
						structure.
					</p>
				}
				accept={handleAccept}
				reject={handleHide}
			/>
		</React.Fragment>
	);
};

export default RemoveFacilityOverrideConfirmationDialog;
